import { ENCRYPTION_ALGORITHM, ENCRYPTION_KEY_LENGTH } from '../constants';

// Check if we're in a server environment
const isServer = typeof process !== 'undefined' && process.versions?.node;

// Generate random string for tokens, codes, etc.
export function generateRandomString(length: number): string {
  if (isServer) {
    // Server-side: use Node.js crypto
    const { randomBytes } = require("crypto");
    return randomBytes(Math.ceil(length / 2))
      .toString('hex')
      .slice(0, length)
      .toUpperCase();
  } else {
    // Client-side: use Web Crypto API
    const array = new Uint8Array(Math.ceil(length / 2));
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
      .slice(0, length)
      .toUpperCase();
  }
}

// Generate secure session ID
export function generateSessionId(): string {
  return generateRandomString(32);
}

// Server-side encryption functions (only available on server)
export const serverEncryption = isServer ? (() => {
  const { randomBytes, createCipheriv, createDecipheriv } = require("crypto");

  // Encryption key - In production, this should be an environment variable
  const ENCRYPTION_KEY = process.env?.ENCRYPTION_KEY || "default-key-change-in-production-32bytes";

  // Ensure the key is the correct length
  const encryptionKey = Buffer.from(ENCRYPTION_KEY.padEnd(ENCRYPTION_KEY_LENGTH, "0").slice(0, ENCRYPTION_KEY_LENGTH));

  return {
    encryptData(data: string): string {
      const iv = randomBytes(16);
      const cipher = createCipheriv(ENCRYPTION_ALGORITHM, encryptionKey, iv);
      let encrypted = cipher.update(data, "utf8", "hex");
      encrypted += cipher.final("hex");
      return iv.toString("hex") + ":" + encrypted;
    },

    decryptData(encryptedData: string): string {
      const parts = encryptedData.split(":");
      const iv = Buffer.from(parts[0], "hex");
      const encrypted = parts[1];
      const decipher = createDecipheriv(ENCRYPTION_ALGORITHM, encryptionKey, iv);
      let decrypted = decipher.update(encrypted, "hex", "utf8");
      decrypted += decipher.final("utf8");
      return decrypted;
    }
  };
})() : null;

// Legacy exports for backward compatibility (server only)
export const encryptData = isServer ? serverEncryption!.encryptData : () => {
  throw new Error('encryptData is only available on the server side');
};

export const decryptData = isServer ? serverEncryption!.decryptData : () => {
  throw new Error('decryptData is only available on the server side');
};